const EARTH_RADIUS_METERS = 6371000

const toRadians = (degrees) => degrees * Math.PI / 180

const hasCoordinates = (point) => {
  return Number.isFinite(parseFloat(point.latitude)) && Number.isFinite(parseFloat(point.longitude))
}

const normalizePostal = (postal) => String(postal === undefined || postal === null ? '' : postal).replace(/\D/g, '')

/**
 * Great-circle distance in meters between two { lat, lng } coordinates
 */
export const haversineMeters = (from, to) => {
  const dLat = toRadians(to.lat - from.lat)
  const dLng = toRadians(to.lng - from.lng)
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(a)))
}

/**
 * Reference coordinate for a postcode: the centroid of the points with the same postcode,
 * else of the points sharing its first 3 digits, else its first 2 digits. Null when nothing matches.
 */
export const resolvePostalReference = (points, postal) => {
  const wanted = normalizePostal(postal)
  if (wanted.length < 2) {
    return null
  }
  const located = (points || []).filter(hasCoordinates)
  const prefixLengths = [wanted.length >= 5 ? 5 : wanted.length, 3, 2]
  for (const length of prefixLengths) {
    if (length > wanted.length) {
      continue
    }
    const prefix = wanted.slice(0, length)
    const matches = located.filter(point => normalizePostal(point.postalCode).slice(0, length) === prefix)
    if (matches.length > 0) {
      return {
        lat: matches.reduce((sum, point) => sum + parseFloat(point.latitude), 0) / matches.length,
        lng: matches.reduce((sum, point) => sum + parseFloat(point.longitude), 0) / matches.length
      }
    }
  }
  return null
}

/**
 * Returns [{ sellingPoint, distance }] sorted by ascending distance (meters) from the reference.
 * Points without coordinates go last.
 */
export const sortByProximity = (points, reference) => {
  return (points || [])
    .map((sellingPoint) => ({
      sellingPoint,
      distance: hasCoordinates(sellingPoint)
        ? haversineMeters(reference, { lat: parseFloat(sellingPoint.latitude), lng: parseFloat(sellingPoint.longitude) })
        : Infinity
    }))
    .sort((a, b) => a.distance - b.distance)
}
